import React, { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polygon } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
})

L.Marker.prototype.options.icon = defaultIcon


const LeafletMap = ({ farmers }) => {

  // Default center to Ghana's coordinates
  const defaultCenter = [7.9465, -1.0232]
  const defaultZoom = 7

  // Function to check if coordinates are valid
  const isValidCoordinate = (lat, lng) => {
    return typeof lat === 'number' && typeof lng === 'number' &&
      !isNaN(lat) && !isNaN(lng) &&
      lat !== 0 && lng !== 0
  }

  // Function to get farmer coordinates
  const getFarmerCoordinates = (farmer) => {
    const lat = parseFloat(farmer.latitude)
    const lng = parseFloat(farmer.longitude)

    if (isValidCoordinate(lat, lng)) {
      return [lat, lng]
    }
    return null
  }

  // Function to get farm coordinates from farmer's farms
  const getFarmCoordinates = (farmer) => {
    if (!farmer.farms || !Array.isArray(farmer.farms)) return []

    return farmer.farms
      .map(farm => {
        const lat = parseFloat(farm.latitude)
        const lng = parseFloat(farm.longitude)

        if (isValidCoordinate(lat, lng)) {
          return {
            ...farm,
            coordinates: [lat, lng]
          }
        }
        return null
      })
      .filter(Boolean)
  }

  return (
    <div className="w-full h-[600px]">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {farmers && farmers
          ?.filter(farmer => {
            // Filter farmers who have valid coordinates (either farmer coordinates or farm coordinates)
            const farmerCoords = getFarmerCoordinates(farmer)
            const farmCoords = getFarmCoordinates(farmer)
            return farmerCoords || farmCoords.length > 0
          })
          ?.map(farmer => {
            const farmerCoords = getFarmerCoordinates(farmer)
            const farmCoords = getFarmCoordinates(farmer)

            return (
              <React.Fragment key={farmer.id}>
                {/* Farmer Marker */}
                {farmerCoords && (
                  <Marker position={farmerCoords}>
                    <Popup>
                      <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-lg">
                        <div className="w-16 h-16 rounded-full bg-[#e6e0f8] flex items-center justify-center border-2 border-white shadow-md mb-2">
                          <img
                            src={farmer.profile_image || '/default-avatar.png'}
                            alt={`${farmer.firstname || 'Farmer'} ${farmer.lastname || ''}`}
                            className="w-14 h-14 rounded-full object-cover"
                            onError={(e) => {
                              e.target.src = '/default-avatar.png'
                            }}
                          />
                        </div>
                        <div className="text-center">
                          <h3 className="font-semibold text-gray-800 text-sm">
                            {farmer.firstname} {farmer.lastname}
                          </h3>
                          <p className="text-xs text-gray-600">
                            {farmer.email || 'No email'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {farmer.phone || 'No phone'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {farmer.community}, {farmer.district}
                          </p>
                          {farmer.farms && farmer.farms.length > 0 && (
                            <p className="text-xs text-blue-600 font-medium">
                              {farmer.farms.length} farm(s)
                            </p>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Farm Markers */}
                {farmCoords.map((farm, index) => (
                  <Marker key={`${farmer.id}-farm-${index}`} position={farm.coordinates}>
                    <Popup>
                      <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-lg">
                        <div className="w-16 h-16 rounded-full bg-[#f0f8e6] flex items-center justify-center border-2 border-white shadow-md mb-2">
                          <span className="text-2xl">🌾</span>
                        </div>
                        <div className="text-center">
                          <h3 className="font-semibold text-gray-800 text-sm">
                            {farm.farm_name || 'Farm'}
                          </h3>
                          <p className="text-xs text-gray-600">
                            Owner: {farmer.firstname} {farmer.lastname}
                          </p>
                          <p className="text-xs text-gray-500">
                            {farm.primary_crop || 'No crop specified'} • {farm.farm_size || 'Unknown'} acres
                          </p>
                          <p className="text-xs text-gray-500">
                            {farm.community}, {farm.district}
                          </p>
                          {farm.production_capacity && (
                            <p className="text-xs text-green-600 font-medium">
                              Capacity: {farm.production_capacity} units
                            </p>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </React.Fragment>
            )
          })}
      </MapContainer>
    </div>
  )
}

export default LeafletMap 